/*
 * Geometria do preview de bordado — ADR-0017.
 *
 * Só matemática e validação: nada aqui toca DOM, canvas ou rede. É o que
 * permite testar fora do navegador a parte em que errar custa pedido errado.
 *
 * As coordenadas da calibragem são normalizadas 0–1 para sobreviver à troca de
 * resolução da foto — a mesma calibragem serve a miniatura e a tela cheia.
 */

(function (window) {
  'use strict';

  const CAMPOS_DE_AREA = ['x', 'y', 'rot', 'skx', 'sky', 'corpo', 'linha', 'gap', 'max'];
  const CAMPOS_DO_ICONE = ['x', 'y', 'rot', 'tamanho'];

  const ROTULOS = {
    esquerdo: 'Peito esquerdo (de quem veste)',
    direito: 'Peito direito (de quem veste)',
  };

  /**
   * Converte a área normalizada em pixels da foto renderizada.
   *
   * Posição e largura máxima escalam pela largura; corpo e entrelinha escalam
   * pela ALTURA, porque é a altura da peça que define o tamanho aparente da
   * letra. Ângulo e inclinação não escalam.
   *
   * @param {object} area
   * @param {number} larguraPx
   * @param {number} alturaPx
   * @returns {object}
   */
  function areaEmPixels(area, larguraPx, alturaPx) {
    return {
      ...area,
      x: area.x * larguraPx,
      y: area.y * alturaPx,
      max: area.max * larguraPx,
      corpo: area.corpo * alturaPx,
      linha: area.linha * alturaPx,
    };
  }

  /**
   * Quebra o texto na largura da faixa bordável.
   *
   * Bordado não parte palavra no meio: palavra sozinha maior que a faixa fica
   * numa linha só e devolve `estourou`, para a UI impedir a confirmação em vez
   * de deixar o erro aparecer na produção.
   *
   * @param {string} texto
   * @param {(t: string) => number} medirLargura
   * @param {number} larguraMaxPx
   * @returns {{linhas: string[], estourou: boolean}}
   */
  function quebrarLinhas(texto, medirLargura, larguraMaxPx) {
    const palavras = String(texto ?? '').trim().split(/\s+/).filter(Boolean);
    if (palavras.length === 0) return { linhas: [], estourou: false };

    const linhas = [];
    let atual = '';

    for (const palavra of palavras) {
      const tentativa = atual ? `${atual} ${palavra}` : palavra;

      if (atual && medirLargura(tentativa) > larguraMaxPx) {
        linhas.push(atual);
        atual = palavra;
      } else {
        atual = tentativa;
      }
    }

    linhas.push(atual);

    return { linhas, estourou: linhas.some((l) => medirLargura(l) > larguraMaxPx) };
  }

  /**
   * Monta os blocos do bordado: cada campo do app vira um bloco com o próprio
   * corpo — o primeiro (o nome) em `corpo`, os demais (a especialidade) em
   * `linha` — e quebra na largura da faixa com o SEU tamanho. É o que a POC
   * fazia e a referência de mercado mostra: nome maior, especialidade menor,
   * um embaixo do outro. Juntar tudo num texto só era o erro.
   *
   * @param {string[]} campos Valores digitados, na ordem do contrato.
   * @param {object} p Área já em pixels (ver areaEmPixels).
   * @param {(texto: string, tamanhoPx: number) => number} medir
   * @returns {{blocos: {linhas: string[], tamanho: number}[], estourou: boolean}}
   */
  function montarBlocos(campos, p, medir) {
    let estourou = false;

    const blocos = (campos ?? [])
      .map((valor, indice) => {
        const tamanho = indice === 0 ? p.corpo : p.linha;
        const quebra = quebrarLinhas(valor, (t) => medir(t, tamanho), p.max);
        if (quebra.estourou) estourou = true;
        return { linhas: quebra.linhas, tamanho };
      })
      .filter((bloco) => bloco.linhas.length > 0);

    return { blocos, estourou };
  }

  /**
   * Área calibrada de um lado do peito.
   * @param {object} calibragem
   * @param {string} lado
   * @returns {object|null}
   */
  function areaDoLado(calibragem, lado) {
    return calibragem?.areas?.[lado] ?? null;
  }

  /**
   * Rótulo do lado, do jeito que a produção precisa ler.
   * @param {string} lado
   * @returns {string}
   */
  function rotuloDoLado(lado) {
    return ROTULOS[lado] ?? '';
  }

  /**
   * Lado oposto do peito: onde o ícone vai quando o nome já ocupa o outro (ENN-92).
   * Só existem `esquerdo` e `direito`; qualquer outro valor não tem oposto.
   * @param {string|null|undefined} lado
   * @returns {string|null}
   */
  function ladoOposto(lado) {
    if (lado === 'esquerdo') return 'direito';
    if (lado === 'direito') return 'esquerdo';

    return null;
  }

  /**
   * Diz se a calibragem pode renderizar, e o motivo quando não pode.
   *
   * Recusa é sempre total: meio preview é pior que nenhum, porque a cliente
   * acredita no que vê e bordado não tem troca.
   *
   * @param {object|null|undefined} valor
   * @returns {{ok: boolean, motivo: string|null}}
   */
  function validarCalibragem(valor) {
    if (!valor || typeof valor !== 'object') return { ok: false, motivo: 'Calibragem ausente' };
    if (valor.versao !== 1) return { ok: false, motivo: `Versão ${valor.versao} não suportada` };
    if (!valor.publicado) return { ok: false, motivo: 'Calibragem em rascunho' };
    if (!valor.foto) return { ok: false, motivo: 'Foto não escolhida' };

    const lados = Array.isArray(valor.lados) ? valor.lados : [];
    if (lados.length === 0) return { ok: false, motivo: 'Lados não configurados' };

    for (const lado of lados) {
      const area = areaDoLado(valor, lado);
      if (!area) return { ok: false, motivo: `Área do lado ${lado} faltando` };

      const faltando = CAMPOS_DE_AREA.filter((campo) => typeof area[campo] !== 'number');
      if (faltando.length > 0) {
        return { ok: false, motivo: `Área do lado ${lado} sem ${faltando.join(', ')}` };
      }
    }

    return { ok: true, motivo: null };
  }

  /**
   * Diz se o bloco `icone` da calibragem pode desenhar o ícone na foto (ENN-95).
   *
   * Independe do texto: o ícone tem `publicado` próprio, e o bloco é opcional.
   * A foto do ícone vive em outro metafield (`custom.bordado_icone_foto`) e quem
   * chama confere se ela veio. Mesma regra do texto: recusa é total.
   *
   * @param {object|null|undefined} icone
   * @returns {{ok: boolean, motivo: string|null}}
   */
  function validarIcone(icone) {
    if (!icone || typeof icone !== 'object') return { ok: false, motivo: 'Ícone sem calibragem' };
    if (icone.publicado !== true) return { ok: false, motivo: 'Ícone em rascunho' };

    const faltando = CAMPOS_DO_ICONE.filter((campo) => typeof icone[campo] !== 'number' || !Number.isFinite(icone[campo]));
    if (faltando.length > 0) return { ok: false, motivo: `Ícone sem ${faltando.join(', ')}` };
    if (icone.tamanho <= 0) return { ok: false, motivo: 'Tamanho do ícone inválido' };

    return { ok: true, motivo: null };
  }

  /**
   * Converte o ícone normalizado em pixels da foto renderizada.
   *
   * Centro e largura escalam pela largura da foto; a altura segue a proporção
   * do PNG do ícone (altura/largura), para o desenho nunca achatar.
   *
   * @param {{x, y, rot, tamanho}} icone
   * @param {number} larguraPx
   * @param {number} alturaPx
   * @param {number} proporcao Altura sobre largura do PNG do ícone.
   * @returns {{x: number, y: number, rot: number, largura: number, altura: number}}
   */
  function iconeEmPixels(icone, larguraPx, alturaPx, proporcao) {
    const largura = icone.tamanho * larguraPx;

    return {
      x: icone.x * larguraPx,
      y: icone.y * alturaPx,
      rot: icone.rot,
      largura,
      altura: largura * (proporcao > 0 ? proporcao : 1),
    };
  }

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.bordadoGeometria = {
    areaEmPixels,
    quebrarLinhas,
    montarBlocos,
    areaDoLado,
    rotuloDoLado,
    ladoOposto,
    validarCalibragem,
    validarIcone,
    iconeEmPixels,
  };
})(window);
